// Imbuements (como no Global): escolhe um equipamento, um espaço (engaste) e o imbuement permitido; mostra itens de
// criatura pedidos (tem / precisa), custo, chance e duração. Visual de encantamento: roxo/ciano com runas brilhando.
// CSS em client/css-parts/forge.css (prefixo .im-).
import { useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { IMBUEMENTS, IMBUE_BASES, TIER_NAMES } from '../../../../shared/src/game/imbuements';
import type { ImbueDef } from '../../../../shared/src/game/imbuements';
import { catalogNow } from '../items';
import { ItemImg } from './ItemSlot';
import { IMBUE_TOKEN_COST } from '../../../../shared/src/game/imbuetokens';

const IMG = '/assets/interface/images';
const IMB = IMG + '/imbuing';
const n = (v: number) => v.toLocaleString('pt-BR');
const hm = (s: number) => `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
const LEVEL_ICON = ['', 'basic', 'intricate', 'powerful'];
const SEEN_KEY = 'nx-help-imbue';
type Send = (m: { op: 'apply' | 'clear'; itemId: number; slot: number; name?: string; tier?: number; protect?: boolean; tokens?: boolean }) => void;

function Level(props: { tier: number }) {
  return props.tier ? <img class="im-lvl" src={`${IMB}/icon-imbuementlevel-${LEVEL_ICON[props.tier]}.png`} alt={TIER_NAMES[props.tier]} title={TIER_NAMES[props.tier]} /> : null;
}

export function Imbue(props: { items: ItemInstance[]; bank: number; send: Send }) {
  const cat = catalogNow();
  const byId = new Map(props.items.map((i) => [i.id, i]));
  const carried = (i: ItemInstance) => { let c: ItemInstance | undefined = i; while (c && c.place === 'container') c = c.parentId ? byId.get(c.parentId) : undefined; return !!c && (c.place === 'equip' || c.place === 'pouch'); };
  const list = props.items.filter((i) => carried(i) && i.place !== 'pouch' && (cat?.get(i.typeId)?.imbueSlots ?? 0) > 0);
  const [sel, setSel] = useState<number | null>(list[0]?.id ?? null);
  const [slot, setSlot] = useState(0);
  const [pick, setPick] = useState<string>('');
  const [protect, setProtect] = useState(false);
  const [useTok, setUseTok] = useState(false);
  // passo a passo: abre sozinho enquanto nenhum item tem imbuement e o jogador não fechou a ajuda
  const fresh = !props.items.some((i) => i.imbues?.some((m) => m));
  const [help, setHelp] = useState(() => { if (!fresh) return false; try { return localStorage.getItem(SEEN_KEY) !== '1'; } catch { return true; } });
  const toggleHelp = () => { const v = !help; setHelp(v); if (!v) { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* sem storage */ } } };

  const it = list.find((i) => i.id === sel);
  const t = it ? cat?.get(it.typeId) : undefined;
  const have = (id: number) => props.items.filter((i) => i.typeId === id && carried(i)).reduce((s, i) => s + i.count, 0);
  // tokens: valem os da mochila e os da Noxara Store Box
  const inBox = (i: ItemInstance) => { let c: ItemInstance | undefined = i; while (c && c.place === 'container') c = c.parentId ? byId.get(c.parentId) : undefined; return c?.place === 'inbox'; };
  const haveTok = (id: number) => props.items.filter((i) => i.typeId === id && (carried(i) || inBox(i))).reduce((s, i) => s + i.count, 0);
  const tokOk = (tier: number) => { const tc = IMBUE_TOKEN_COST[tier]; return !!tc && haveTok(tc[0]) >= tc[1]; };
  const allowed = t ? IMBUEMENTS.filter((d) => (t.imbueAllowed?.[d.slotKey] ?? 0) >= d.tier) : [];
  const d = allowed.find((x) => `${x.name}|${x.tier}` === pick);
  const base = d ? IMBUE_BASES.find((b) => b.tier === d.tier) : undefined;
  const cur = it?.imbues?.[slot];
  const baseOf = (tier: number) => IMBUE_BASES.find((b) => b.tier === tier);
  // imbuements agrupados pelo nome (Scorch, Vampirism...) com os níveis permitidos
  const groups: Array<{ name: string; defs: ImbueDef[] }> = [];
  for (const x of allowed) { const g = groups.find((y) => y.name === x.name); if (g) g.defs.push(x); else groups.push({ name: x.name, defs: [x] }); }
  const canMat = (x: ImbueDef) => x.items.every(([id, c]) => have(id!) >= c!) || tokOk(x.tier);
  const total = base ? base.price + (protect ? base.protect : 0) : 0;
  const tc = d ? IMBUE_TOKEN_COST[d.tier] : undefined;
  const matsOk = d ? (useTok ? tokOk(d.tier) : !d.items.some(([id, c]) => have(id!) < c!)) : false;
  const goldOk = props.bank >= total;
  const activeCount = list.reduce((s, i) => s + (i.imbues?.filter((m) => m).length ?? 0), 0);
  const slotsTotal = list.reduce((s, i) => s + (cat?.get(i.typeId)?.imbueSlots ?? 0), 0);

  return (
    <div class="im">
      {/* cabeçalho: círculo de runas girando */}
      <div class="im-head">
        <div class="im-circle" aria-hidden="true">
          <span class="im-ring" /><span class="im-ring r2" />
          <span class="im-core">✦</span>
          <i class="im-rune u1">ᚱ</i><i class="im-rune u2">ᛟ</i><i class="im-rune u3">ᚦ</i><i class="im-rune u4">ᛉ</i>
        </div>
        <div class="im-head-txt">
          <div class="im-title">Altar de Imbuements
            <button class={'im-help' + (help ? ' on' : '')} onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="im-sub">Itens de criatura + gold = poder por <b>{hm(IMBUE_BASES[0]!.duration)}</b> de caça</div>
          <div class="im-wallet">
            <span class="im-chip gold" title="Os imbuements são pagos com o gold do banco"><img src={`${IMG}/icon-goldcoin.png`} alt="" /><b>{n(props.bank)}</b> gp</span>
            <span class="im-chip" title="Espaços com imbuement ativo nos seus equipamentos"><i>✦</i><b>{activeCount}</b>/{slotsTotal} engastes ativos</span>
          </div>
        </div>
      </div>

      {help && (
        <div class="im-steps">
          <div><i>🛡️</i><b>1. Escolha o item</b><small>Equipamentos com espaço de imbuement que estejam com você. Cada item tem espaços próprios e só aceita algumas categorias.</small></div>
          <div><i>💠</i><b>2. Escolha o engaste</b><small>Clique num espaço livre. Um espaço ocupado pode ser limpo por {n(IMBUE_BASES[0]!.remove)} gp.</small></div>
          <div><i>🦴</i><b>3. Junte os itens</b><small>Cada nível (Basic, Intricate, Powerful) pede itens de criatura da mochila e gold do banco. Ou troque os itens por tokens dos bosses: 2 / 4 Silver Tokens (Basic / Intricate) ou 5 Gold Tokens (Powerful).</small></div>
          <div><i>⏳</i><b>4. Aplique</b><small>Dura {hm(IMBUE_BASES[0]!.duration)} de uso: o tempo só corre caçando com o item equipado. A proteção garante o sucesso.</small></div>
        </div>
      )}

      <div class="im-body">
        <div class="im-list">
          <div class="im-cap">Seus equipamentos</div>
          {list.map((i) => {
            const ti = cat?.get(i.typeId), ns = ti?.imbueSlots ?? 0;
            return (
              <button key={i.id} class={'im-it' + (i.id === sel ? ' on' : '')} onClick={() => { setSel(i.id); setSlot(0); setPick(''); }} title={ti?.name}>
                <span class="item-slot"><ItemImg typeId={i.typeId} count={1} /></span>
                <span class="im-it-txt"><b>{ti?.name}</b><small>{i.place === 'equip' ? 'equipado' : 'na mochila'}</small></span>
                <span class="im-dots">{Array.from({ length: ns }, (_, k) => <i key={k} class={i.imbues?.[k] ? 'on' : ''} />)}</span>
              </button>
            );
          })}
          {!list.length && <p class="muted small im-empty">Nenhum equipamento com espaço de imbuement com você.</p>}
        </div>

        <div class="im-detail">
          {!it || !t ? <p class="muted small im-empty">Escolha um item.</p> : <>
            <div class="im-hero">
              <div class="im-altar"><span class="im-big"><ItemImg typeId={it.typeId} count={1} /></span><span class="im-glow" /></div>
              <div class="im-hero-txt">
                <b class="im-name">{t.name}</b>
                <small>{t.imbueSlots} espaço(s){it.place === 'equip' ? ' · equipado' : ' · na mochila (o tempo só corre equipado)'}</small>
                <div class="im-sockets">
                  {Array.from({ length: t.imbueSlots ?? 0 }, (_, i) => {
                    const m = it.imbues?.[i], dur = m ? baseOf(m.tier)?.duration ?? IMBUE_BASES[0]!.duration : 0;
                    return (
                      <button key={i} class={'im-socket' + (slot === i ? ' on' : '') + (m ? ' full' : '')} onClick={() => setSlot(i)}
                        title={m ? `${TIER_NAMES[m.tier]} ${m.name} (${hm(m.left)})` : `Espaço ${i + 1} livre`}>
                        <span class="im-gem">{m ? <b>{m.name.slice(0, 2)}</b> : <img src={`${IMB}/imbuing-slot-empty.png`} alt="" />}</span>
                        <span class="im-sock-txt">
                          {m ? <><b>{m.name}</b><Level tier={m.tier} /><small>{hm(m.left)}</small>
                            <span class="im-time"><em style={{ width: Math.min(100, m.left / dur * 100) + '%' }} /></span></>
                            : <><b>Espaço {i + 1}</b><small>livre</small></>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {cur ? (
              <div class="im-active">
                <div class="im-active-h"><Level tier={cur.tier} /><b>{TIER_NAMES[cur.tier]} {cur.name}</b><span>{hm(cur.left)} restantes</span></div>
                <small>{IMBUEMENTS.find((x) => x.name === cur.name && x.tier === cur.tier)?.desc}</small>
                <button class="tb-button im-clear" onClick={() => props.send({ op: 'clear', itemId: it.id, slot })}>
                  <img src={`${IMB}/imbuing-icon-remove-active.png`} alt="" />Limpar este espaço ({n(IMBUE_BASES[0]!.remove)} gp)</button>
              </div>
            ) : <>
              <div class="im-cap">Imbuements aceitos neste item <small>{groups.length}</small></div>
              {!groups.length && <p class="muted small im-empty">Este item não aceita imbuements.</p>}
              <div class="im-choices">
                {groups.map((g) => (
                  <div key={g.name} class={'im-choice' + (d?.name === g.name ? ' on' : '')}>
                    <span class="im-ch-name"><b>{g.name}</b><small>{(d?.name === g.name ? d : g.defs[g.defs.length - 1])!.desc}</small></span>
                    <span class="im-ch-tiers">
                      {g.defs.map((x) => {
                        const v = `${x.name}|${x.tier}`;
                        return (
                          <button key={x.tier} class={'im-tierbtn' + (pick === v ? ' on' : '') + (canMat(x) ? ' ok' : '')} onClick={() => setPick(pick === v ? '' : v)}
                            title={`${TIER_NAMES[x.tier]} ${x.name}${canMat(x) ? ' · você tem os itens' : ' · faltam itens'}`}>
                            <Level tier={x.tier} />{TIER_NAMES[x.tier]}
                          </button>
                        );
                      })}
                    </span>
                  </div>
                ))}
              </div>

              {d && base && (
                <div class="im-recipe">
                  <div class="im-recipe-h"><Level tier={d.tier} /><b>{TIER_NAMES[d.tier]} {d.name}</b><small>{d.desc}</small></div>
                  {tc && (
                    <div class="im-pay" role="tablist">
                      <button class={!useTok ? 'on' : ''} onClick={() => setUseTok(false)}>🦴 Itens de criatura</button>
                      <button class={useTok ? 'on' : ''} onClick={() => setUseTok(true)}>
                        <span class="item-slot im-tokico"><ItemImg typeId={tc[0]} count={1} /></span>{tc[1]}x {cat?.get(tc[0])?.name ?? 'token'}</button>
                    </div>
                  )}
                  {useTok && tc ? (
                    <div class="im-mats">
                      <div class={'im-mat' + (tokOk(d.tier) ? ' ok' : ' bad')} title="Mochila ou Noxara Store Box. Os tokens caem de todos os bosses.">
                        <span class="item-slot"><ItemImg typeId={tc[0]} count={tc[1]} /></span>
                        <span class="im-mat-txt"><small>{cat?.get(tc[0])?.name ?? 'Token'} (mochila + Store Box)</small><b>{haveTok(tc[0])}</b> / {tc[1]}</span>
                        <span class="im-matbar"><em style={{ width: Math.min(100, haveTok(tc[0]) / tc[1] * 100) + '%' }} /></span>
                      </div>
                      <small class="im-tokhint">Silver Token: Basic e Intricate · Gold Token: Powerful. Caem de todos os bosses.</small>
                    </div>
                  ) : <div class="im-mats">{d.items.map(([id, c]) => {
                    const h = have(id!), ok = h >= c!;
                    return (
                      <div key={id} class={'im-mat' + (ok ? ' ok' : ' bad')} title={cat?.get(id!)?.name}>
                        <span class="item-slot"><ItemImg typeId={id!} count={c!} /></span>
                        <span class="im-mat-txt"><small>{cat?.get(id!)?.name ?? 'Item'}</small><b>{h}</b> / {c}</span>
                        <span class="im-matbar"><em style={{ width: Math.min(100, h / c! * 100) + '%' }} /></span>
                      </div>
                    );
                  })}</div>}
                  <div class="im-stats">
                    <div><small>Chance</small><b class={protect ? 'sure' : ''}>{protect ? 100 : base.chance}%</b></div>
                    <div><small>Duração</small><b>{hm(base.duration)}</b></div>
                    <div><small>Custo</small><b class={goldOk ? '' : 'bad'}>{n(total)} gp</b></div>
                  </div>
                  <button class={'im-protect' + (protect ? ' on' : '')} onClick={() => setProtect(!protect)}>
                    <img src={`${IMB}/imbuing-icon-useprotection-${protect ? 'active' : 'disabled'}.png`} alt="" />
                    <span><input type="checkbox" checked={protect} tabIndex={-1} readOnly /> <b>Proteção (+{n(base.protect)} gp)</b><small>100% de sucesso</small></span>
                  </button>
                  <button class={'im-go' + (goldOk && matsOk ? ' ready' : '')} disabled={!goldOk || !matsOk}
                    onClick={() => props.send({ op: 'apply', itemId: it.id, slot, name: d.name, tier: d.tier, protect, tokens: useTok })}>
                    <img src={`${IMB}/imbuing-icon-imbue-${goldOk && matsOk ? 'active' : 'disabled'}.png`} alt="" />
                    Aplicar ({n(total)} gp · {protect ? 100 : base.chance}%)</button>
                  {(!goldOk || !matsOk) && <small class="im-why">{!matsOk ? (useTok ? 'Faltam tokens.' : 'Faltam itens de criatura (em vermelho).') : 'Gold insuficiente no banco.'}</small>}
                </div>
              )}
            </>}
          </>}
        </div>
      </div>
    </div>
  );
}
